import {
  ClockCircleOutlined,
  KeyOutlined,
  MobileOutlined,
  NumberOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import { enumManagers } from '@chirp/enums';
import React from 'react';

import {
  renderUserOrUserRoleString,
  UserOrUserRoleLink,
} from '../../components/UserOrUserRoleLink';
import type { SmartLockLogTableFragment, SmartLockLogType } from '../../graphql/api/generated';
import { PermissionScope_enum } from '../../graphql/hasura/generated';
import { PropertyModel } from '../Property/model';
import { SmartLockModel } from '../SmartLock/model';
import { UnitModel } from '../Unit/model';
import { UserModel } from '../User/model';

import model from './model';

const SMART_LOCK_LOG_TYPE_ICON_MAP: { [key in SmartLockLogType]: React.ReactNode | null } = {
  LOCK_MANUAL: <KeyOutlined />,
  LOCK_VIA_APP: <MobileOutlined />,
  LOCK_VIA_AUTO_LOCK: <ClockCircleOutlined />,
  LOCK_VIA_KEYPAD: <NumberOutlined />,
  PIN_CODE_MANAGEMENT_CREATE: <NumberOutlined />,
  PIN_CODE_MANAGEMENT_DELETE: <NumberOutlined />,
  UNLOCK_MANUAL: <KeyOutlined />,
  UNLOCK_VIA_APP: <MobileOutlined />,
  UNLOCK_VIA_KEYPAD: <NumberOutlined />,
  USER_MANAGEMENT_ADD: <UserAddOutlined />,
  USER_MANAGEMENT_REMOVE: <UserDeleteOutlined />,
};

export const idColumn = model.createIdColumn();

export const propertyColumn = model.createColumn<SmartLockLogTableFragment>({
  filterOptions: {
    type: 'MODEL_SELECT',
    key: 'propertyId',
    model: PropertyModel,
  },
  enabled: ({ currentPermissionScope }) => (
    currentPermissionScope === PermissionScope_enum.GLOBAL ||
    currentPermissionScope === PermissionScope_enum.ORGANIZATION
  ),
  render: ({ smartLock }) => PropertyModel.routes.renderRowLink(smartLock.unit.property),
});

export const unitColumn = model.createColumn<SmartLockLogTableFragment>({
  filterOptions: {
    type: 'MODEL_SELECT',
    key: 'unitId',
    model: UnitModel,
  },
  render: ({ smartLock }) => UnitModel.routes.renderRowLink(smartLock.unit),
});

export const smartLockColumn = model.createColumn<SmartLockLogTableFragment>({
  filterOptions: {
    type: 'MODEL_SELECT',
    key: 'smartLockId',
    model: SmartLockModel,
  },
  render: ({ smartLock }) => SmartLockModel.routes.renderRowLink(smartLock),
});

export const typeColumn = model.createColumn<SmartLockLogTableFragment>({
  title: 'Type',
  filterOptions: {
    type: 'ENUM',
    key: 'type',
    enumTable: 'SmartLockLogType',
  },
  render: ({ type }) => {
    const label = enumManagers.SmartLockLogType.getLabel(type);
    const icon = SMART_LOCK_LOG_TYPE_ICON_MAP[type];

    return (
      <>
        {icon ? (
          <span style={{ marginRight: 5 }}>{icon}</span>
        ) : null}
        {label}
      </>
    );
  },
  renderString: ({ type }) => enumManagers.SmartLockLogType.getLabel(type) || '',
});

// @TODO: Differentiate "Acting User" and "Affected User" columns?
export const actingUserColumn = model.createColumn<SmartLockLogTableFragment>({
  filterOptions: {
    type: 'MODEL_SELECT',
    key: 'actingUserId',
    model: UserModel,
  },
  render: ({ actingUserId, actingUser, actingUserRoleId }) => {
    return (
      <UserOrUserRoleLink
        userId={actingUserId}
        user={actingUser}
        userRoleId={actingUserRoleId}
      />
    );
  },
  renderString: async ({ actingUserId, actingUser, actingUserRoleId }) => {
    return await renderUserOrUserRoleString({
      userId: actingUserId,
      user: actingUser,
      userRoleId: actingUserRoleId,
    });
  },
});
